// The main path through a process: from the start most items begin at, along the connection most items take at each
// step, to where the work ends or loops back. The Actions graph's main flow level reads each model by it, and the
// model canvas's main path layout lines each part of a model up along it. Pure, so it can be tested without a page

// a connection on from a node: where it goes, the items a year taking it, and what it is (an edge, say)
export interface PathStep<T, E> {
    to: T;
    items: number;
    share?: number;     // to choose between connections carrying the same items: the larger is taken
    via?: E;
}

export interface MainPath<T, E> {
    nodes: T[];
    via: E[];           // the connections between them, in order
}

// the main path from the busiest of the starts
export const mainPath = <T, E>(starts: T[], itemsAt: (node: T) => number, next: (node: T) => PathStep<T, E>[]): MainPath<T, E> => {
    const first = [...starts].sort((a, b) => itemsAt(b) - itemsAt(a))[0];
    const path: MainPath<T, E> = { nodes: [], via: [] };
    const on = new Set<T>();
    for (let at: T | undefined = first; at !== undefined && !on.has(at);) {
        path.nodes.push(at);
        on.add(at);
        const step = next(at).filter(s => !on.has(s.to)).sort((a, b) => (b.items || 0) - (a.items || 0) || (b.share || 0) - (a.share || 0))[0];
        if (step?.via !== undefined) path.via.push(step.via);
        at = step?.to;
    }
    return path;
};

// the main path of each part of a process not connected to the rest, longest first. A part starts where its work
// starts (or, with none marked, where nothing comes in); one that only loops starts anywhere in it
export const mainPaths = <T, E>(nodes: T[], itemsAt: (node: T) => number, next: (node: T) => PathStep<T, E>[], previous: (node: T) => T[],
    isStart: (node: T) => boolean): MainPath<T, E>[] => {
    const all = new Set(nodes);
    const seen = new Set<T>();
    const paths: MainPath<T, E>[] = [];
    nodes.forEach(node => {
        if (seen.has(node)) return;
        const part: T[] = [];
        const queue = [node];
        seen.add(node);
        while (queue.length) {
            const at = queue.shift()!;
            part.push(at);
            [...next(at).map(s => s.to), ...previous(at)].filter(n => all.has(n) && !seen.has(n)).forEach(n => {
                seen.add(n);
                queue.push(n);
            });
        }
        const marked = part.filter(isStart);
        const starts = marked.length ? marked : part.filter(n => !previous(n).some(p => all.has(p)));
        paths.push(mainPath(starts.length ? starts : part, itemsAt, n => next(n).filter(s => all.has(s.to))));
    });
    return paths.sort((a, b) => b.nodes.length - a.nodes.length);
};
